import { Text } from "@mantine/core";
import { Select } from "src/components/shared/MantineCombobox";
import { useCallback, useMemo } from "react";
import { useFormContext } from "react-hook-form";
import { useInputProps } from "src/generator/hooks/useInputProps";
import useLiftingEquipmentTargets from "src/generator/hooks/useLiftingEquipmentTargets";
import { useLogbookRefsContext } from "src/generator/Logbook/LogbookRefsContext";
import { useGetApi } from "src/hooks";
import useTriggerInlineSave from "src/hooks/useTriggerInlineSave";
import { useDispatch } from "src/redux/hooks";
import { setEditing } from "src/redux/slices/entrySlice";
import { getYachtId } from "src/utils";

interface ILiftingEquipmentItem {
  name: string;
  description: string;
  certificates: string[];
  distinct: string;
}

interface ILiftingEquipment {
  list: ILiftingEquipmentItem[];
}

const mapLiftingEquipment = (res: any): ILiftingEquipment => {
  const data = res?.list?.[0];
  const list = data?.data;
  if (!data || !list || list.length === 0) return { list: [] };

  return {
    list: list.map((item: ILiftingEquipmentItem) => ({
      ...item,
      certificates:
        item.certificates && item.certificates.length > 0
          ? item.certificates
          : [""],
    })),
  };
};

interface Props {
  id: string;
  value: any;
  required?: boolean;
  sectionIndex?: number;
  rowIndex?: number;
  rowUuid?: string;
  sharedIndex?: number;
  subSectionIndex?: number;
  feature?: string;
}

const LiftingEquipmentSelect = ({
  id,
  value,
  required,
  sectionIndex,
  rowIndex,
  rowUuid,
  sharedIndex,
  subSectionIndex,
  feature,
}: Props) => {
  const dispatch = useDispatch();
  const { setValue } = useFormContext();
  const triggerSave = useTriggerInlineSave(sectionIndex);
  const targets = useLiftingEquipmentTargets({
    uuid: id,
    sectionIndex,
    rowIndex,
    rowUuid,
    sharedIndex,
    subSectionIndex,
  });

  const { data: equipment } = useGetApi<ILiftingEquipment>({
    request: "get-lifting-equipment-list",
    body: { params: { yachtid: getYachtId() } },
    mapper: mapLiftingEquipment,
  });
  const { onChange: defaultOnChange, ...inputProps } = useInputProps(
    id,
    value,
    required,
  );

  const logbookRefsProps = useLogbookRefsContext(id);

  const getCurrentValue = useCallback(() => {
    if (!inputProps.value) return null;

    if (typeof inputProps.value === "string") {
      const normalized = inputProps.value.trim();
      return normalized !== "" ? normalized : null;
    }

    if (typeof inputProps.value === "object") {
      const key =
        typeof inputProps.value?.key === "string"
          ? inputProps.value.key.trim()
          : "";
      const value =
        typeof inputProps.value?.value === "string"
          ? inputProps.value.value.trim()
          : "";
      if (key !== "") return key;
      if (value !== "") return value;
      return null;
    }

    return null;
  }, [inputProps.value]);

  const options = useMemo(() => {
    const mapped = equipment?.list
      .map(item => ({
        label: item?.name?.trim() ?? "",
        value: item?.name?.trim() ?? "",
      }))
      .filter(item => item.label !== "");

    const currentValue = getCurrentValue();
    if (
      currentValue &&
      !mapped?.some(
        item => item.value.toLowerCase() === String(currentValue).toLowerCase(),
      )
    ) {
      const normalized = String(currentValue).trim();
      if (normalized !== "") {
        mapped?.push({ label: normalized, value: normalized });
      }
    }

    return mapped;
  }, [equipment, getCurrentValue]);

  const selectedItem = useMemo(() => {
    const currentValue = getCurrentValue();
    if (!currentValue) return null;

    return (
      equipment?.list.find(
        item => item.name.toLowerCase() === String(currentValue).toLowerCase(),
      ) ?? null
    );
  }, [getCurrentValue, equipment]);

  const updateTargetsFromSelection = useCallback(
    (item: ILiftingEquipmentItem | undefined) => {
      if (feature !== "setCertsFromLifting" || !item || !targets) {
        return;
      }

      if (targets.certificates) {
        const certs = (item.certificates ?? []).filter(Boolean).join("\n");
        setValue(targets.certificates, certs);
      }

      if (targets.distinct) {
        setValue(targets.distinct, item.distinct ?? "");
      }
    },
    [feature, setValue, targets],
  );

  const markChanged = useCallback(() => {
    triggerSave();
    dispatch(setEditing(true));
  }, [triggerSave, dispatch]);

  const handleChange = useCallback(
    (selected: string | null) => {
      if (!selected) {
        defaultOnChange("");
        markChanged();
        return;
      }

      const matched = equipment?.list.find(item => item.name === selected);

      if (matched) {
        defaultOnChange({ key: selected, value: selected });
        updateTargetsFromSelection(matched);
      } else {
        defaultOnChange(selected);
      }

      markChanged();
    },
    [defaultOnChange, equipment, markChanged, updateTargetsFromSelection],
  );

  const handleBlur = useCallback(
    (event: React.FocusEvent<HTMLInputElement>) => {
      inputProps.onBlur();

      const typedValue = event.currentTarget.value.trim();
      if (!typedValue) {
        return;
      }

      const currentValue = getCurrentValue();
      const hasExactOption = equipment?.list.some(
        item => item.name.toLowerCase() === typedValue.toLowerCase(),
      );

      if (!hasExactOption && currentValue !== typedValue) {
        defaultOnChange(typedValue);
        markChanged();
      }
    },
    [inputProps, getCurrentValue, equipment, defaultOnChange, markChanged],
  );

  return (
    <div style={{ width: "100%" }}>
      <Select
        searchable
        creatable
        shouldCreate={(query, data) =>
          query.trim() !== "" &&
          !data.some(item => item?.label?.toLowerCase() === query.toLowerCase())
        }
        getCreateLabel={query => `"${query}"`}
        data={options ?? []}
        onChange={handleChange}
        dropdownPosition="top"
        withinPortal={true}
        zIndex={1000}
        {...inputProps}
        value={getCurrentValue()}
        ref={el => logbookRefsProps.ref(el)}
        onKeyDown={logbookRefsProps.onKeyDown}
        onBlur={handleBlur}
      />
      {selectedItem?.description && (
        <Text size="xs" c="dimmed" mt={4}>
          {selectedItem.description}
        </Text>
      )}
    </div>
  );
};

export default LiftingEquipmentSelect;
